<template>
  <div v-if="!isAdmin" class="min-h-screen bg-gray-100 flex items-center justify-center">
    <div class="text-center">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
      <p class="text-gray-600">You do not have permission to access this page.</p>
      <router-link
        to="/"
        class="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
      >
        Return Home
      </router-link>
    </div>
  </div>

  <div v-else class="min-h-screen bg-gray-100">
    <div class="py-10">
      <header>
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 class="text-3xl font-bold leading-tight tracking-tight text-gray-900">Admin Dashboard</h1>
        </div>
      </header>
      <main>
        <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <!-- Stats -->
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mt-8">
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <dt class="truncate text-sm font-medium text-gray-500">Total Users</dt>
              <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{{ stats.totalUsers }}</dd>
            </div>
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <dt class="truncate text-sm font-medium text-gray-500">Free Users</dt>
              <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{{ stats.freeUsers }}</dd>
            </div>
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <dt class="truncate text-sm font-medium text-gray-500">Business Users</dt>
              <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{{ stats.businessUsers }}</dd>
            </div>
            <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
              <dt class="truncate text-sm font-medium text-gray-500">Pro Users</dt>
              <dd class="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{{ stats.proUsers }}</dd>
            </div>
          </div>

          <!-- Users Table -->
          <div class="mt-8 bg-white shadow sm:rounded-lg">
            <div class="px-4 py-5 sm:p-6">
              <div class="sm:flex sm:items-center">
                <div class="sm:flex-auto">
                  <h2 class="text-base font-semibold leading-6 text-gray-900">Users</h2>
                  <p class="mt-2 text-sm text-gray-700">A list of all users including their name, email, subscription tier, and usage.</p>
                </div>
              </div>

              <!-- Search and Filter -->
              <div class="mt-4 flex space-x-4">
                <div class="flex-1">
                  <input 
                    type="text" 
                    v-model="searchQuery" 
                    placeholder="Search users..." 
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
                <div>
                  <select 
                    v-model="tierFilter" 
                    class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  >
                    <option value="">All Tiers</option>
                    <option value="free">Free</option>
                    <option value="business">Business</option>
                    <option value="pro">Pro</option>
                  </select>
                </div>
              </div>

              <!-- Table -->
              <div class="mt-6 flow-root">
                <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <table class="min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Email</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Subscription</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Searches Used</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Content Created</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Joined</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200">
                        <tr v-for="user in filteredUsers" :key="user.id">
                          <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                            {{ user.email }}
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm">
                            <span :class="tierClass(user.subscription_tier)">
                              {{ user.subscription_tier }}
                            </span>
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {{ user.searches_used }} / {{ user.searches_limit === -1 ? '∞' : user.searches_limit }}
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {{ user.content_creations_used }} / {{ user.content_limit === -1 ? '∞' : user.content_limit }}
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {{ formatDate(user.created_at) }}
                          </td>
                          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 space-x-2">
                            <button
                              v-if="!userAdminStatus[user.id]"
                              @click="makeAdmin(user)"
                              class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700"
                            >
                              Make Admin
                            </button>
                            <button
                              v-if="userAdminStatus[user.id]"
                              @click="removeAdmin(user)"
                              class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700"
                            >
                              Remove Admin
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { supabase } from '../services/supabase'
import { useNotificationStore } from '../stores/notification'
import { useSubscriptionStore } from '../stores/subscription'
import { useAuthStore } from '../stores/auth'
import { useKeywordStore } from '../stores/keyword'
import { useLocationStore } from '../stores/location'
import { useOpenAIStore } from '../stores/openai'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

const router = useRouter()
const authStore = useAuthStore()
const keywordStore = useKeywordStore()
const locationStore = useLocationStore()
const openaiStore = useOpenAIStore()
const user = computed(() => authStore.user)
const { keywords, loading } = storeToRefs(keywordStore)

const notificationStore = useNotificationStore()
const subscriptionStore = useSubscriptionStore()

const users = ref([])
const searchQuery = ref('')
const tierFilter = ref('')
const isAdmin = ref(false)
const userAdminStatus = ref({})

const stats = computed(() => {
  return {
    totalUsers: users.value.length,
    freeUsers: users.value.filter(u => u.subscription_tier === 'free').length,
    businessUsers: users.value.filter(u => u.subscription_tier === 'business').length,
    proUsers: users.value.filter(u => ['pro', 'admin'].includes(u.subscription_tier)).length,
  }
})

const filteredUsers = computed(() => {
  return users.value
    .filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchQuery.value.toLowerCase())
      const matchesTier = !tierFilter.value || user.subscription_tier === tierFilter.value
      return matchesSearch && matchesTier
    })
})

function tierClass(tier) {
  const baseClasses = 'inline-flex rounded-full px-2 text-xs font-semibold leading-5 '
  switch (tier) {
    case 'free':
      return baseClasses + 'bg-gray-100 text-gray-800'
    case 'business':
      return baseClasses + 'bg-blue-100 text-blue-800'
    case 'pro':
      return baseClasses + 'bg-green-100 text-green-800'
    case 'admin':
      return baseClasses + 'bg-purple-100 text-purple-800'
    default:
      return baseClasses + 'bg-gray-100 text-gray-800'
  }
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  try {
    // Try parsing as ISO string first
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      // If invalid, try parsing as Unix timestamp
      const timestamp = parseInt(dateString)
      if (!isNaN(timestamp)) {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      return 'N/A'
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'N/A'
  }
}

async function loadUsers() {
  try {
    const { data, error } = await supabase.rpc('get_all_users')
    
    if (error) {
      console.error('Error loading users:', error)
      throw error
    }

    // Transform the data to match the component's expectations
    users.value = data.map(user => ({
      id: user.id,
      email: user.email,
      subscription_tier: user.is_admin ? 'admin' : user.tier,
      searches_used: user.searches_used || 0,
      content_creations_used: user.content_creations_used || 0,
      created_at: user.created_at,
      searches_limit: (user.is_admin || user.tier === 'pro') ? -1 : (user.tier === 'business' ? 100 : 10),
      content_limit: (user.is_admin || user.tier === 'pro') ? -1 : (user.tier === 'business' ? 50 : 5)
    }))

    // Update admin status map
    const newAdminStatus = {}
    data.forEach(user => {
      newAdminStatus[user.id] = user.is_admin
    })
    userAdminStatus.value = newAdminStatus

  } catch (error) {
    console.error('Error loading users:', error)
    notificationStore.addNotification({
      type: 'error',
      message: `Failed to load users: ${error.message}`
    })
  }
}

// Add admin check function
async function checkAdminStatus() {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      isAdmin.value = false
      return
    }

    // Check admin status in user_subscriptions table
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('user_subscriptions')
      .select('tier')
      .eq('user_id', session.user.id)
      .single()

    if (subscriptionError) {
      console.error('Error checking subscription:', subscriptionError)
      isAdmin.value = false
      return
    }

    isAdmin.value = subscriptionData?.tier === 'admin'

  } catch (error) {
    console.error('Error in checkAdminStatus:', error)
    isAdmin.value = false
  }
}

// Also fix the onMounted function to load users if admin
onMounted(async () => {
  await checkAdminStatus()
  if (!isAdmin.value) {
    router.push('/')
    return
  }
  
  try {
    await loadUsers()
  } catch (error) {
    console.error('Error loading data:', error)
    notificationStore.addNotification({
      type: 'error',
      message: `Failed to load users: ${error.message}`
    })
  }
})

async function isUserAdmin(user) {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('tier')
      .eq('user_id', user.id)
      .single()

    if (error) {
      console.error('Error checking admin status:', error)
      return false
    }

    return data?.tier === 'admin'
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

async function makeAdmin(user) {
  try {
    // Use RPC function instead of direct table access
    const { data, error } = await supabase
      .rpc('make_user_admin', {
        target_user_id: user.id
      })

    if (error) {
      throw error
    }

    // Refresh the entire user list to ensure we have the latest data
    await loadUsers()

    notificationStore.addNotification({
      type: 'success',
      message: `Successfully made ${user.email} an admin with unlimited access`
    })

  } catch (error) {
    console.error('Error making user admin:', error)
    notificationStore.addNotification({
      type: 'error',
      message: `Failed to make user admin: ${error.message}`
    })
  }
}

async function removeAdmin(user) {
  try {
    // Use RPC function instead of direct table access
    const { data, error } = await supabase
      .rpc('remove_user_admin', {
        target_user_id: user.id
      })

    if (error) {
      throw error
    }

    // Refresh the entire user list to ensure we have the latest data
    await loadUsers()

    notificationStore.addNotification({
      type: 'success',
      message: `Successfully removed admin access from ${user.email}`
    })

  } catch (error) {
    console.error('Error removing admin access:', error)
    notificationStore.addNotification({
      type: 'error',
      message: `Failed to remove admin access: ${error.message}`
    })
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
